/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { EmailRegex } from '@msdyn365-commerce-modules/retail-actions';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import ReCAPTCHA from 'react-google-recaptcha';
import { computed, reaction, when } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import * as MsDyn365 from '@msdyn365-commerce/core';

import { ICheckoutGuestProfileEmarsysData } from './checkout-guest-profile-emarsys.data';
import { ICheckoutGuestProfileEmarsysProps } from './checkout-guest-profile-emarsys.props.autogenerated';
import getAddContactInfo, { IAddContactInfo } from './components/get-add-contact-info';
import getShowContactInfo, { IShowContactInfo } from './components/get-show-contact-info';

export * from './components/get-add-contact-info';
export * from './components/get-show-contact-info';

interface ICheckoutGuestProfileState {
    email: string;
    TermsAndConditionsChecked: boolean;
    reCaptchaChecked: boolean;
    rCCError: boolean;

}

export interface ICheckoutGuestProfileModuleProps extends ICheckoutGuestProfileEmarsysProps<ICheckoutGuestProfileEmarsysData>, IModuleStateProps { }

export interface ICheckoutGuestProfileViewProps extends ICheckoutGuestProfileEmarsysProps<ICheckoutGuestProfileEmarsysData>, ICheckoutGuestProfileState {
    moduleProps: IModuleProps;
    showContactInfo?: IShowContactInfo;
    addContactInfo?: IAddContactInfo;
    reCaptcha: React.ReactNode;
    checkBox: React.ReactNode;

    onChange?(email: string): void;
    onEdit?(): void;
    onSubmit?(): void;
    onCancel?(): void;
}

/**
 *
 * CheckoutGuestProfile component.
 * @extends {React.PureComponent<ICheckoutGuestProfileEmarsysProps<ICheckoutGuestProfileEmarsysData>, ICheckoutGuestProfileState>}
 */
// @ts-expect-error
@withModuleState
@observer
class CheckoutGuestProfile extends React.Component<ICheckoutGuestProfileModuleProps, ICheckoutGuestProfileState> {
    public state: ICheckoutGuestProfileState = {
        email:
            (this.props.context &&
                this.props.context.request &&
                this.props.context.request.user &&
                this.props.context.request.user.isAuthenticated &&
                this.props.context.request.user.emailAddress) ||
            '',
        TermsAndConditionsChecked: false,
        reCaptchaChecked: false,
        rCCError: false,
    };

    private readonly inputRef: React.RefObject<HTMLInputElement> = React.createRef();
    private _TermsAndConditionsRef: React.RefObject<HTMLInputElement> = React.createRef();
    private _recaptchaRef: React.RefObject<ReCAPTCHA> = React.createRef();
    private recaptchaErrorMessage: string | undefined;
    // private responseMessage: string | undefined;


    @computed get isDataReady(): boolean {
        return (this.props.data.checkout.result && this.props.data.checkout.status) === 'SUCCESS';
    }

    public componentDidMount(): void {
        when(
            () => this.isDataReady,
            async () => {
                await this.init();
            }
        );

        reaction(
            () => this.props.data.checkout.result?.isExpressCheckoutApplied,
            async isExpressCheckoutApplied => {
                if (isExpressCheckoutApplied && !this.props.data.checkout.result?.isExpressAddressApplied) {
                    const email = this.props.data.checkout.result?.emailAddressFromExpressCheckout ?? '';

                    await this.submitEmail(email);
                }
            }
        );
    }

    public shouldComponentUpdate(nextProps: ICheckoutGuestProfileModuleProps, nextState: ICheckoutGuestProfileState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const {
            config: { className },
            resources,
            moduleState: { isReady, hasError, hasExternalSubmitGroup }
        } = this.props;
        const email = this.props.data.checkout.result?.guestCheckoutEmail ?? '';
        // const checkBox : this._renderCheckbox();

        const viewProps: ICheckoutGuestProfileViewProps = {
            ...this.props,
            ...this.state,
            onChange: this.onChange,
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit,
            checkBox: this._renderCheckbox(),
            reCaptcha: this._renderReCaptcha(),


            moduleProps: {
                moduleProps: this.props,
                className: classnames('ms-checkout-guest-profile', className, isReady ? 'show' : 'add')
            },
            showContactInfo: isReady
                ? getShowContactInfo({
                    canEdit: !hasExternalSubmitGroup,
                    email,
                    resources,
                    onEdit: this.onEdit
                })
                : undefined,
            addContactInfo: !isReady
                ? getAddContactInfo({
                    email: this.state.email,
                    hasError,
                    resources,
                    inputRef: this.inputRef,
                    onChange: this.onChange,
                    canSubmit: !hasExternalSubmitGroup,
                    onSubmit: this.onSubmit,
                    canCancel: !hasExternalSubmitGroup && !!email,
                    onCancel: this.onCancel
                })
                : undefined
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }


    private _renderCheckbox(): React.ReactNode {
        return (
            <div className="checkbox-termsAndConditions">
                <div className='checkbox-termsAndConditions-content'>
                    <label >
                        <input
                            ref={this._TermsAndConditionsRef}
                            type='checkbox'
                            className='termsAndConditions-text'
                            aria-label='Accept terms And Conditions'
                            aria-checked='false'
                            onChange={this._checkBoxOnChange()}
                            id='termsAndConditionsCheckbox'
                        /> {this.props.config.termsAndConditionsText}<a href={this.props.config.termsAndConditionsLink}>terms & conditions</a>

                    </label>

                </div>

            </div>
        )
    }

    private _renderReCaptcha(): React.ReactNode {
        return (
            <div>
                <div className=''>
                    {this.state.rCCError ? this._renderRecaptchaAlert('recaptcha-error') : ''}
                    <ReCAPTCHA
                        ref={this._recaptchaRef}
                        sitekey={this.props.config.reCaptchaSiteKey!}
                        size="invisible"
                        onChange={() => this._submit()}
                    />
                </div>
            </div>
        );
    }

    private _renderRecaptchaAlert(className: string): JSX.Element {

        if (this.props.config.reCaptchaOption) {
            if (!this.state.reCaptchaChecked) {
                this.recaptchaErrorMessage = this.props.resources.recaptchaErrorMessage;
            }
        }

        const rCCError = this.state.rCCError;
        return (
            <>
                {rCCError && (
                    <span className={`${className}__alert`} role='alert' aria-live='assertive'>
                        {
                            this.recaptchaErrorMessage &&
                            <>
                                <span className={`${className}__alert-icon`} aria-hidden='true' />
                                {this.recaptchaErrorMessage}
                            </>
                        }
                    </span>
                )}
            </>
        );
    }

    private readonly init = async (): Promise<void> => {
        const checkoutState = this.props.data.checkout.result;

        const hasInvoiceLine = checkoutState?.checkoutCart.hasInvoiceLine;

        const emailOnCart = checkoutState?.checkoutCart.cart.ReceiptEmail;
        const email = hasInvoiceLine
            ? this.props.context.request.user.emailAddress ?? ''
            : emailOnCart ??
            (checkoutState?.isExpressCheckoutApplied ? checkoutState.emailAddressFromExpressCheckout : undefined) ??
            this.state.email;

        if (email) {
            await this.updateGuestProfile(email);
            checkoutState?.updateHasGuestCheckoutEmail({ newHasGuestCheckoutEmail: true });
        } else {
            checkoutState?.updateHasGuestCheckoutEmail({ newHasGuestCheckoutEmail: false });
        }

        const isEmailValid = this.isEmailValid(email);
        this.props.moduleState.init({
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit,
            status: hasInvoiceLine ? 'disabled' : email && isEmailValid ? 'ready' : 'updating',
            hasError: !!email && !isEmailValid
        });
    };

    private readonly onChange = (rowEmail: string): void => {
        const email = (rowEmail || '').replace(new RegExp('[<>]', 'gi'), '');
        this.setState({ email });
        if (this.props.moduleState.hasError) {
            // Clear error during user updating the email
            this.props.moduleState.setHasError(false);
        }
    };

    private _checkBoxOnChange = () => (e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = this._TermsAndConditionsRef.current && this._TermsAndConditionsRef.current.checked ? this._TermsAndConditionsRef.current.checked : false;
        this.setState({TermsAndConditionsChecked: isChecked });
    }
    private readonly isEmailValid = (email: string): boolean => {
        return EmailRegex.defaultRegex.test(email);
    };

    private readonly updateGuestProfile = async (newGuestCheckoutEmail: string): Promise<void> => {
        if (this.props.data.checkout.result) {
            await this.props.data.checkout.result.updateGuestCheckoutEmail({ newGuestCheckoutEmail });
            this.setState({ email: newGuestCheckoutEmail });
        }
    };

    private readonly onEdit = (): void => {
        this.props.telemetry.information('Guest profile onEdit is called.');
        this.props.moduleState.onUpdating();
    };

    private readonly onSubmit = async (): Promise<void> => {
        this.props.telemetry.information('Guest profile onSubmit is called.');
        const { email } = this.state;
        await this.submitEmail(email);
        if(this.state.TermsAndConditionsChecked) {
            try {
                this._recaptchaRef.current?.execute()
            } catch(e) {
                console.log(e, 'Please fill the form!');
            }
        }
    };

    private async _submit(): Promise<void> {
        let emarsysGroupId = this.props.config.emarsysGroup;
        const { email } = this.state;
        try {
            const response = await MsDyn365.commerceApiRequest(
                this.props.context.request,
                'PostCheckoutGuestProfileEmarsys',
                'post',
                {
                    emailAddress: email,
                    emarsysGroupId: emarsysGroupId,
                    optIn:true
                }
            ) as any;

            if (response?.data.Result !== 'Error') {
                console.log('emasys success')
            } else {
                console.log('this is an emasys error')
            }
            this._recaptchaRef.current?.reset();
        } catch (e) {
            console.log('rechatcha error')
        }
       
    }
    /**
     * Submit the email address.
     * @param email - The email to be submitted.
     *
     */
    private readonly submitEmail = async (email: string): Promise<void> => {
        const isValid = this.isEmailValid(email);
        if (!isValid) {
            this.props.moduleState.setHasError(true);

            const input = this.inputRef.current?.focus && (this.inputRef.current as HTMLElement);
            input?.focus();

        } else {
            this.props.moduleState.onReady();
            await this.updateGuestProfile(email);

        }
    };

    private readonly onCancel = (): void => {
        this.props.telemetry.information('Guest profile onCancel is called.');

        // Reset to the saved value
        if (!this.props.data.checkout.result) {
            return;
        }

        const email = this.props.data.checkout.result.guestCheckoutEmail;
        if (!email) {
            // It has no saved email, set to status updating and request user to fill the email form
            this.props.moduleState.onUpdating();
        } else {
            // It has saved email, set to status ready
            this.setState({
                email
            });
            this.props.moduleState.setHasError(false);
            this.props.moduleState.onReady();
        }
    };
}

export default CheckoutGuestProfile;
